/**
 * space_hud.ts — the JTL-style space combat HUD (DOM overlay, no engine deps).
 *
 * Modeled on Star Wars Galaxies: Jump to Lightspeed's flight interface:
 *   • center aim reticle with the ship's forward speed under it (JTL's
 *     crosshairs "indicate your ship's forward speed"),
 *   • a LEAD reticle ahead of the target ("hold fire until your aim closes
 *     on the lead target reticle"),
 *   • target brackets + an off-screen direction arrow (Tab targeting),
 *   • FRONT and REAR shield readouts (JTL shields are front/rear arcs),
 *   • weapon CAPACITOR + BOOSTER energy bars (firing drains the capacitor;
 *     the booster is burst speed on its own reservoir),
 *   • a throttle gauge (JTL flight is throttle-based, 0..1), and
 *   • a circular sensor radar (contacts in ship-local space, forward = up).
 *
 * The module owns ONLY presentation. SpaceModule feeds it numbers; nothing
 * here reads game state, so it stays trivially testable and swap-safe.
 */

export interface SpaceHudStatus {
  hull: number; hullMax: number;
  shieldF: number; shieldR: number; shieldMax: number;
  cap: number; capMax: number;
  booster: number; boosterMax: number;
  throttle: number;       // 0..1
  speed: number;          // m/s
  boost: boolean;
  /** Overcruise wind-up factor (1..10) — shown once it leaves ×1. */
  cruise: number;
  matchSpeed: boolean;
  bounty: number;
  target: { name: string; dist: number; hp: number; hpMax: number } | null;
  /** Hyperdrive nav readout (N cycles, J jumps). state: '' | 'ALIGN' | 'SPOOL' | 'HYPER'. */
  nav: { name: string; dist: number; state: string } | null;
}

/** One radar contact, already transformed into ship-local axes (x right, z back, y up). */
export interface RadarContact { x: number; y: number; z: number; kind: 'pirate' | 'station' | 'remote' | 'beacon' | 'field' | 'hazard' | 'wreck' | 'planet' }

const RADAR_PX = 148;

function bar(label: string, color: string): { row: HTMLDivElement; fill: HTMLSpanElement } {
  const row = document.createElement('div');
  row.style.cssText = 'display:flex;align-items:center;gap:6px;margin-top:3px';
  const lab = document.createElement('span');
  lab.textContent = label;
  lab.style.cssText = 'width:52px;font:600 10px ui-monospace,monospace;color:#9fb4cc;text-align:right;letter-spacing:.4px';
  const track = document.createElement('span');
  track.style.cssText = 'flex:1;display:block;height:7px;background:#101a2a;border:1px solid #22344c;border-radius:4px;overflow:hidden';
  const fill = document.createElement('span');
  fill.style.cssText = `display:block;height:100%;width:100%;background:${color};transition:width .12s linear`;
  track.appendChild(fill);
  row.append(lab, track);
  return { row, fill };
}

export class SpaceHud {
  private root: HTMLDivElement | null = null;
  private reticle: HTMLDivElement | null = null;
  private speedEl: HTMLDivElement | null = null;
  private statusPanel: HTMLDivElement | null = null;
  private bars: Record<'hull' | 'shieldF' | 'shieldR' | 'cap' | 'booster', HTMLSpanElement> | null = null;
  private throttleEl: HTMLDivElement | null = null;
  private navEl: HTMLDivElement | null = null;
  private bountyEl: HTMLDivElement | null = null;
  private targetPanel: HTMLDivElement | null = null;
  private bracket: HTMLDivElement | null = null;
  private lead: HTMLDivElement | null = null;
  private arrow: HTMLDivElement | null = null;
  private radar: HTMLCanvasElement | null = null;
  private promptEl: HTMLDivElement | null = null;
  private toastEl: HTMLDivElement | null = null;
  private hintEl: HTMLDivElement | null = null;

  mount(): void {
    if (this.root) return;
    const root = document.createElement('div');
    root.id = 'space-hud';
    root.style.cssText = 'position:fixed;inset:0;z-index:34;pointer-events:none;font:12px system-ui';
    this.root = root;

    // Center reticle: outer ring + crosshair ticks, speed readout beneath.
    const ret = document.createElement('div');
    ret.style.cssText =
      'position:absolute;left:50%;top:50%;width:34px;height:34px;transform:translate(-50%,-50%);' +
      'border:1px solid rgba(140,190,255,.85);border-radius:50%;' +
      'box-shadow:0 0 6px rgba(80,140,255,.35), inset 0 0 4px rgba(80,140,255,.3)';
    for (const [w, h, l, t] of [[10, 1, -14, 16], [10, 1, 36, 16], [1, 10, 16, -14], [1, 10, 16, 36]] as const) {
      const tick = document.createElement('span');
      tick.style.cssText = `position:absolute;width:${w}px;height:${h}px;left:${l}px;top:${t}px;background:rgba(140,190,255,.85)`;
      ret.appendChild(tick);
    }
    this.reticle = ret;
    const speed = document.createElement('div');
    speed.style.cssText =
      'position:absolute;left:50%;top:calc(50% + 30px);transform:translateX(-50%);' +
      'font:600 12px ui-monospace,monospace;color:#bcd7ff;text-shadow:0 1px 3px #000';
    this.speedEl = speed;

    // Bottom-left ship status: hull, front/rear shields, capacitor, booster, throttle.
    const panel = document.createElement('div');
    panel.style.cssText =
      'position:absolute;left:16px;bottom:16px;width:220px;background:rgba(8,13,22,.78);' +
      'border:1px solid #24405e;border-radius:10px;padding:8px 12px 10px';
    const title = document.createElement('div');
    title.textContent = 'SHIP STATUS';
    title.style.cssText = 'font:700 10px ui-monospace,monospace;color:#6f8cae;letter-spacing:1.2px';
    panel.appendChild(title);
    const hull = bar('HULL', 'linear-gradient(90deg,#57a35a,#7bc86c)');
    const sf = bar('F·SHLD', 'linear-gradient(90deg,#3b7fb8,#7ec8e3)');
    const sr = bar('R·SHLD', 'linear-gradient(90deg,#3b7fb8,#7ec8e3)');
    const cap = bar('CAP', 'linear-gradient(90deg,#b8973b,#e3c97e)');
    const bst = bar('BOOST', 'linear-gradient(90deg,#8a4fb8,#c07ee3)');
    for (const b of [hull, sf, sr, cap, bst]) panel.appendChild(b.row);
    this.bars = { hull: hull.fill, shieldF: sf.fill, shieldR: sr.fill, cap: cap.fill, booster: bst.fill };
    const thr = document.createElement('div');
    thr.style.cssText = 'margin-top:6px;font:600 11px ui-monospace,monospace;color:#bcd7ff';
    panel.appendChild(thr);
    this.throttleEl = thr;
    const nav = document.createElement('div');
    nav.style.cssText = 'margin-top:3px;font:600 11px ui-monospace,monospace;color:#9fe3c0;display:none';
    panel.appendChild(nav);
    this.navEl = nav;
    this.statusPanel = panel;

    // Top-center: bounty tally (kept from v1 — the run's earnings).
    const bounty = document.createElement('div');
    bounty.style.cssText =
      'position:absolute;top:14px;left:calc(50% + 96px);' + // beside the top-center wallet chip (ladder v2)
      'font:600 12px ui-monospace,monospace;color:#dfe9f5;text-shadow:0 1px 3px #000';
    this.bountyEl = bounty;

    // Top-right: target readout.
    const tp = document.createElement('div');
    tp.style.cssText =
      'position:absolute;top:14px;right:16px;min-width:170px;display:none;background:rgba(8,13,22,.78);' +
      'border:1px solid #5e2430;border-radius:10px;padding:8px 12px;font:12px ui-monospace,monospace;color:#f0d7d7';
    this.targetPanel = tp;

    // Target bracket (screen-space box), lead pip, off-screen arrow.
    const br = document.createElement('div');
    br.style.cssText =
      'position:absolute;width:34px;height:34px;display:none;transform:translate(-50%,-50%);' +
      'border:1.5px solid rgba(255,110,110,.9);border-radius:4px;' +
      'clip-path:polygon(0 0,30% 0,30% 12%,12% 12%,12% 30%,0 30%,0 0,100% 0,100% 30%,88% 30%,88% 12%,70% 12%,70% 0,100% 0,100% 100%,70% 100%,70% 88%,88% 88%,88% 70%,100% 70%,100% 100%,0 100%,0 70%,12% 70%,12% 88%,30% 88%,30% 100%,0 100%)';
    this.bracket = br;
    const lead = document.createElement('div');
    lead.style.cssText =
      'position:absolute;width:10px;height:10px;display:none;transform:translate(-50%,-50%);' +
      'border:1.5px solid rgba(255,220,120,.95);border-radius:50%;box-shadow:0 0 5px rgba(255,220,120,.5)';
    this.lead = lead;
    const arrow = document.createElement('div');
    arrow.style.cssText =
      'position:absolute;left:50%;top:50%;width:0;height:0;display:none;' +
      'border-left:7px solid transparent;border-right:7px solid transparent;border-bottom:13px solid rgba(255,110,110,.9)';
    this.arrow = arrow;

    // Bottom-right: circular sensor radar.
    const radar = document.createElement('canvas');
    radar.width = radar.height = RADAR_PX;
    radar.style.cssText = `position:absolute;right:16px;bottom:16px;width:${RADAR_PX}px;height:${RADAR_PX}px`;
    this.radar = radar;

    // Docking prompt + controls hint.
    const prompt = document.createElement('div');
    prompt.style.cssText =
      'position:absolute;bottom:120px;left:50%;transform:translateX(-50%);display:none;' +
      'background:rgba(9,13,20,.92);border:1px solid #35506a;border-radius:8px;padding:6px 14px;color:#dfe9f5';
    this.promptEl = prompt;
    const hint = document.createElement('div');
    hint.innerHTML = 'W/S throttle · mouse steer · A/D roll · R level · <b>TAB</b> target · SPACE fire · SHIFT boost · <b>M</b> map · <b>N</b> nav · <b>J</b> hyperdrive · G repair · X dock';
    hint.style.cssText =
      'position:absolute;bottom:4px;left:50%;transform:translateX(-50%);white-space:nowrap;' +
      'font:11px system-ui;color:#93a7c0;opacity:.65;text-shadow:0 1px 3px #000';
    this.hintEl = hint;

    root.append(ret, speed, panel, bounty, tp, br, lead, arrow, radar, prompt, hint);
    document.body.appendChild(root);
  }

  unmount(): void {
    this.root?.remove();
    this.root = null;
    this.toastEl?.remove();
    this.toastEl = null;
  }

  /** ~5 Hz numeric refresh — bars, throttle/speed, bounty, target readout. */
  setStatus(s: SpaceHudStatus): void {
    if (!this.bars) return;
    const pct = (v: number, m: number): string => `${Math.max(0, Math.min(100, (v / m) * 100))}%`;
    this.bars.hull.style.width = pct(s.hull, s.hullMax);
    this.bars.hull.style.filter = s.hull / s.hullMax < 0.3 ? 'hue-rotate(-90deg)' : '';
    this.bars.shieldF.style.width = pct(s.shieldF, s.shieldMax);
    this.bars.shieldR.style.width = pct(s.shieldR, s.shieldMax);
    this.bars.cap.style.width = pct(s.cap, s.capMax);
    this.bars.booster.style.width = pct(s.booster, s.boosterMax);
    if (this.throttleEl) {
      this.throttleEl.textContent =
        `THR ${Math.round(s.throttle * 100)}%${s.boost ? ' ⚡BOOST' : ''}` +
        `${s.cruise > 1.15 ? ` · CRUISE ×${s.cruise.toFixed(1)}` : ''}${s.matchSpeed ? ' · MATCH' : ''}`;
    }
    if (this.speedEl) this.speedEl.textContent = `${Math.round(s.speed)} m/s`;
    if (this.navEl) {
      if (s.nav) {
        const d = s.nav.dist;
        const distTxt = d >= 10_000 ? `${(d / 1000).toFixed(0)} km` : `${Math.round(d)} m`;
        this.navEl.textContent = `NAV ${s.nav.name} · ${distTxt}${s.nav.state ? ` · ${s.nav.state}` : ''}`;
        this.navEl.style.display = 'block';
      } else this.navEl.style.display = 'none';
    }
    if (this.bountyEl) this.bountyEl.textContent = `bounty ${s.bounty} ⓒ`;
    if (this.targetPanel) {
      if (s.target) {
        const pips = '◆'.repeat(Math.max(0, s.target.hp)) + '◇'.repeat(Math.max(0, s.target.hpMax - s.target.hp));
        this.targetPanel.replaceChildren(); // rebuild with text nodes — names are data, never markup
        const nameEl = document.createElement('div');
        nameEl.style.cssText = 'color:#ff9d9d;font-weight:700';
        nameEl.textContent = s.target.name;
        const infoEl = document.createElement('div');
        infoEl.style.cssText = 'opacity:.8';
        infoEl.textContent = `${Math.round(s.target.dist)} m · ${pips}`;
        this.targetPanel.append(nameEl, infoEl);
        this.targetPanel.style.display = 'block';
      } else this.targetPanel.style.display = 'none';
    }
  }

  /** Per-frame marker placement (screen px). Pass null to hide a marker. */
  setTargetMarkers(
    bracket: { x: number; y: number } | null,
    lead: { x: number; y: number } | null,
    arrowAngleRad: number | null,
  ): void {
    if (this.bracket) {
      this.bracket.style.display = bracket ? 'block' : 'none';
      if (bracket) { this.bracket.style.left = `${bracket.x}px`; this.bracket.style.top = `${bracket.y}px`; }
    }
    if (this.lead) {
      this.lead.style.display = lead ? 'block' : 'none';
      if (lead) { this.lead.style.left = `${lead.x}px`; this.lead.style.top = `${lead.y}px`; }
    }
    if (this.arrow) {
      this.arrow.style.display = arrowAngleRad !== null ? 'block' : 'none';
      if (arrowAngleRad !== null) {
        // Slide the arrow around a ring near the reticle, pointing outward.
        const r = 72;
        const x = Math.sin(arrowAngleRad) * r;
        const y = -Math.cos(arrowAngleRad) * r;
        this.arrow.style.transform = `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) rotate(${arrowAngleRad}rad)`;
      }
    }
  }

  /** ~10 Hz radar sweep. Contacts are ship-local (x right, z back, y up). */
  drawRadar(contacts: RadarContact[], rangeM: number): void {
    const cv = this.radar;
    const g = cv?.getContext('2d');
    if (!cv || !g) return;
    const c = RADAR_PX / 2;
    g.clearRect(0, 0, RADAR_PX, RADAR_PX);
    g.fillStyle = 'rgba(8,13,22,.78)';
    g.beginPath(); g.arc(c, c, c - 1, 0, Math.PI * 2); g.fill();
    g.strokeStyle = '#24405e';
    g.beginPath(); g.arc(c, c, c - 1, 0, Math.PI * 2); g.stroke();
    g.strokeStyle = 'rgba(36,64,94,.55)';
    g.beginPath(); g.arc(c, c, (c - 1) / 2, 0, Math.PI * 2); g.stroke();
    g.beginPath(); g.moveTo(c, 4); g.lineTo(c, RADAR_PX - 4); g.moveTo(4, c); g.lineTo(RADAR_PX - 4, c); g.stroke();
    // own-ship chevron
    g.fillStyle = '#bcd7ff';
    g.beginPath(); g.moveTo(c, c - 5); g.lineTo(c - 4, c + 4); g.lineTo(c + 4, c + 4); g.closePath(); g.fill();
    const colors: Record<RadarContact['kind'], string> = {
      pirate: '#ff6e6e', station: '#7ec8e3', remote: '#7bc86c', beacon: '#ffd97e', field: '#8a97a8',
      hazard: '#b48ae3', wreck: '#c9a37e', planet: '#e8d9a0',
    };
    for (const ct of contacts) {
      const px = c + (ct.x / rangeM) * (c - 6);
      const py = c + (ct.z / rangeM) * (c - 6);   // local -z is forward → up on the scope
      const d2 = (px - c) ** 2 + (py - c) ** 2;
      if (d2 > (c - 5) ** 2) { // clamp to rim (still shows bearing)
        const s = (c - 5) / Math.sqrt(d2);
        g.globalAlpha = 0.45;
        g.fillStyle = colors[ct.kind];
        g.fillRect(c + (px - c) * s - 1.5, c + (py - c) * s - 1.5, 3, 3);
        g.globalAlpha = 1;
        continue;
      }
      // above = brighter, below = dimmer (poor-man's sphere)
      g.globalAlpha = ct.y > 40 ? 1 : ct.y < -40 ? 0.5 : 0.8;
      g.fillStyle = colors[ct.kind];
      g.beginPath(); g.arc(px, py, ct.kind === 'planet' ? 4 : ct.kind === 'station' ? 3.4 : 2.6, 0, Math.PI * 2); g.fill();
      g.globalAlpha = 1;
    }
  }

  prompt(text: string | null): void {
    if (!this.promptEl) return;
    this.promptEl.style.display = text ? 'block' : 'none';
    if (text) this.promptEl.textContent = text;
  }

  toast(text: string): void {
    if (!this.toastEl) {
      this.toastEl = document.createElement('div');
      this.toastEl.style.cssText =
        'position:fixed;top:84px;left:max(490px, calc(50% - 260px));z-index:36;' + // centered on wide screens; never under the top-left status panel (≤470 px)
        'max-width:520px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;' +
        'background:rgba(20,32,52,.95);border:1px solid #5a7ef0;border-radius:8px;padding:6px 16px;' +
        'font:600 12px system-ui;color:#dfe9ff;transition:opacity .4s';
      document.body.appendChild(this.toastEl);
    }
    this.toastEl.textContent = text;
    this.toastEl.style.opacity = '1';
    window.setTimeout(() => { if (this.toastEl) this.toastEl.style.opacity = '0'; }, 3200);
  }
}
